import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = { name: "", email: "", password: "password123", auth_method: "local", role_category: "", role_level: "", programs: [], department: "", country: "India", status: "active", is_admin: false };

export default function AdminUsers({ refs }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...empty, role_category: refs.roleCategories[0]?.id || "", role_level: refs.roleLevels[0]?.id || "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u, password: "" }); setOpen(true); };

  const toggleProgram = (pid) => setForm((f) => ({ ...f, programs: f.programs.includes(pid) ? f.programs.filter((x) => x !== pid) : [...f.programs, pid] }));

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{users.length} users</p>
        <Button onClick={openCreate} data-testid="admin-create-user"><Plus className="w-4 h-4 mr-1.5" /> New User</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Auth</TableHead><TableHead>Role</TableHead>
              <TableHead>Level</TableHead><TableHead>Programs</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="admin-users-table">
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{u.name}</span>
                    {u.is_admin && <ShieldCheck className="w-3.5 h-3.5 text-primary" />}
                  </div>
                  <span className="text-xs text-slate-400">{u.email}</span>
                </TableCell>
                <TableCell><Badge variant="outline" className={u.auth_method === "sso" ? "bg-violet-50 text-violet-700 border-violet-200" : ""}>{u.auth_method?.toUpperCase()}</Badge></TableCell>
                <TableCell className="text-sm">{refs.rcName(u.role_category)}</TableCell>
                <TableCell className="text-sm text-slate-500">{refs.rlName(u.role_level)?.split(" ")[0]}</TableCell>
                <TableCell className="text-xs text-slate-500">{(u.programs || []).map((p) => refs.programName(p)).join(", ")}</TableCell>
                <TableCell><StatusPill status={u.status} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(u)} className="text-rose-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit User" : "Create User"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" className="mt-1" /></div>
            </div>
            {!editing && (
              <div>
                <Label>Authentication Method</Label>
                <div className="flex gap-2 mt-1">
                  {["local", "sso"].map((m) => (
                    <button key={m} onClick={() => setForm({ ...form, auth_method: m })}
                      className={`flex-1 text-sm rounded-lg border px-3 py-2 capitalize ${form.auth_method === m ? "border-primary bg-primary/5 text-primary font-medium" : "border-slate-200 text-slate-500"}`}>{m}</button>
                  ))}
                </div>
                {form.auth_method === "sso" ? (
                  <p className="text-xs text-slate-400 mt-1.5">User will authenticate via enterprise SSO. No password is stored.</p>
                ) : (
                  <div className="mt-2"><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="user-form-password" /></div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role Category</Label>
                <Select value={form.role_category} onValueChange={(v) => setForm({ ...form, role_category: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-form-role"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{refs.roleCategories.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role Level</Label>
                <Select value={form.role_level} onValueChange={(v) => setForm({ ...form, role_level: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-form-level"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{refs.roleLevels.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1" /></div>
              <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
            </div>
            <div>
              <Label>Assigned Programs</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {refs.programs.map((p) => (
                  <button key={p.id} onClick={() => toggleProgram(p.id)}
                    className={`text-xs rounded-full border px-3 py-1.5 ${form.programs.includes(p.id) ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-500"}`}>{p.name}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><Label>Active</Label><p className="text-xs text-slate-400">Inactive users cannot log in</p></div>
              <Switch checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><Label>Administrator</Label><p className="text-xs text-slate-400">Full access, bypasses content restrictions</p></div>
              <Switch checked={form.is_admin} onCheckedChange={(v) => setForm({ ...form, is_admin: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="user-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
